<script>
  // Material Design 3 date picker with optional time, packaged like an M3
  // outlined text field. Clicking the field opens a docked calendar panel.
  //
  // Value format: ISO-like "YYYY-MM-DDTHH:mm" (matches what <input type=datetime-local>
  // produces, so server-side handlers don't need to change). Time portion is
  // optional -- if `withTime` is false (default), value is "YYYY-MM-DD".
  import { onMount, tick } from 'svelte';

  let {
    label,
    value = $bindable(''),
    withTime = true,
    min = '',
    max = '',
  } = $props();

  // Parse the optional bounds once per render. Compared at day precision in
  // the calendar grid so a date is disabled when *no minute* of that day is
  // in range. Time inputs then constrain to the minute boundary on the day
  // currently being chosen.
  const minDate = $derived(min ? parse(min) : null);
  const maxDate = $derived(max ? parse(max) : null);

  function dayKey(d) {
    return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
  }
  function isDisabledDay(day) {
    const cell = new Date(viewYear, viewMonth, day);
    const k = dayKey(cell);
    if (minDate && k < dayKey(minDate)) return true;
    if (maxDate && k > dayKey(maxDate)) return true;
    return false;
  }
  function isPrevMonthDisabled() {
    if (!minDate) return false;
    // The last day of the previous month is what we'd land on.
    const last = new Date(viewYear, viewMonth, 0);
    return dayKey(last) < dayKey(minDate);
  }
  function isNextMonthDisabled() {
    if (!maxDate) return false;
    // The first day of the next month.
    const first = new Date(viewYear, viewMonth + 1, 1);
    return dayKey(first) > dayKey(maxDate);
  }

  let open = $state(false);
  let trigger = $state(null);
  let panel = $state(null);
  let panelStyle = $state('');
  let mobile = $state(false);

  // The currently displayed month in the calendar (independent of selected value).
  let viewYear = $state(0);
  let viewMonth = $state(0); // 0-11

  // Time inputs (separate from date so partial picks are possible).
  let h = $state('00');
  let m = $state('00');

  function parse(v) {
    if (!v) return null;
    const d = new Date(v);
    if (isNaN(d.getTime())) return null;
    return d;
  }

  function toIso(d, hh, mm) {
    const yyyy = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const da = String(d.getDate()).padStart(2, '0');
    if (!withTime) return `${yyyy}-${mo}-${da}`;
    return `${yyyy}-${mo}-${da}T${hh}:${mm}`;
  }

  function syncFromValue() {
    const d = parse(value) || new Date();
    viewYear = d.getFullYear();
    viewMonth = d.getMonth();
    if (withTime && parse(value)) {
      h = String(d.getHours()).padStart(2, '0');
      m = String(d.getMinutes()).padStart(2, '0');
    }
  }

  async function toggle() {
    if (open) { close(); return; }
    syncFromValue();
    mobile = window.innerWidth < 600;
    open = true;
    await tick();
    position();
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', position);
    window.addEventListener('scroll', position, true);
  }

  function close() {
    open = false;
    document.removeEventListener('mousedown', onDocMouseDown);
    document.removeEventListener('keydown', onKey);
    window.removeEventListener('resize', position);
    window.removeEventListener('scroll', position, true);
  }

  // Position the docked panel so it stays inside the viewport: shift left if
  // it overflows the right edge, flip above the trigger if it overflows the
  // bottom edge. Mobile uses a centered modal style instead (handled in CSS).
  function position() {
    if (!trigger || !panel || mobile) { panelStyle = ''; return; }
    const t = trigger.getBoundingClientRect();
    const p = panel.getBoundingClientRect();
    const margin = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Horizontal: prefer left-aligned with trigger; clamp to viewport.
    let left = t.left;
    if (left + p.width + margin > vw) left = vw - p.width - margin;
    if (left < margin) left = margin;

    // Vertical: prefer below trigger; flip above if it doesn't fit.
    let top = t.bottom + 4;
    if (top + p.height + margin > vh && t.top - p.height - 4 > margin) {
      top = t.top - p.height - 4;
    }
    if (top < margin) top = margin;

    panelStyle = `top:${top}px; left:${left}px;`;
  }

  function onDocMouseDown(e) {
    if (trigger?.contains(e.target) || panel?.contains(e.target)) return;
    close();
  }
  function onKey(e) { if (e.key === 'Escape') close(); }

  function prevMonth() {
    if (viewMonth === 0) { viewMonth = 11; viewYear--; } else { viewMonth--; }
  }
  function nextMonth() {
    if (viewMonth === 11) { viewMonth = 0; viewYear++; } else { viewMonth++; }
  }

  function pickDay(day) {
    if (isDisabledDay(day)) return;
    const d = new Date(viewYear, viewMonth, day);
    value = toIso(d, h, m);
    if (!withTime) close();
  }

  function clear() { value = ''; close(); }
  function done() { close(); }

  // Calendar grid math
  const monthName = $derived(new Date(viewYear, viewMonth, 1).toLocaleString(undefined, { month: 'long', year: 'numeric' }));
  const weekdays = ['S','M','T','W','T','F','S'];
  const grid = $derived.by(() => {
    const first = new Date(viewYear, viewMonth, 1);
    const startDow = first.getDay();
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  });

  const selected = $derived(parse(value));
  const today = new Date();

  function isToday(day) {
    return day === today.getDate() && viewMonth === today.getMonth() && viewYear === today.getFullYear();
  }
  function isSelected(day) {
    if (!selected) return false;
    return day === selected.getDate() && viewMonth === selected.getMonth() && viewYear === selected.getFullYear();
  }

  // Display string in the field
  const display = $derived.by(() => {
    if (!value) return '';
    const d = parse(value);
    if (!d) return value;
    return withTime ? d.toLocaleString() : d.toLocaleDateString();
  });

  onMount(() => () => close());
</script>

<div class="md-df">
  <button bind:this={trigger} type="button" class="md-df-trigger" class:open onclick={toggle}>
    <span class="md-df-text" class:placeholder={!display}>{display || ' '}</span>
    <span class="md-df-label">{label}</span>
    <span class="material-symbols-outlined md-df-icon">event</span>
  </button>

  {#if open}
    {#if mobile}<div class="md-df-scrim" onclick={close} role="presentation"></div>{/if}
    <div bind:this={panel} class="md-df-panel" class:mobile style={panelStyle}>
      <header class="md-df-header">
        <button type="button" class="md-df-iconbtn" onclick={prevMonth} disabled={isPrevMonthDisabled()} aria-label="Previous month">
          <span class="material-symbols-outlined">chevron_left</span>
        </button>
        <span class="md-df-month">{monthName}</span>
        <button type="button" class="md-df-iconbtn" onclick={nextMonth} disabled={isNextMonthDisabled()} aria-label="Next month">
          <span class="material-symbols-outlined">chevron_right</span>
        </button>
      </header>

      <div class="md-df-weekdays">
        {#each weekdays as w}<span>{w}</span>{/each}
      </div>

      <div class="md-df-grid">
        {#each grid as cell, i (i)}
          {#if cell === null}
            <span class="md-df-cell empty"></span>
          {:else}
            <button
              type="button"
              class="md-df-cell"
              class:today={isToday(cell)}
              class:selected={isSelected(cell)}
              class:disabled={isDisabledDay(cell)}
              disabled={isDisabledDay(cell)}
              onclick={() => pickDay(cell)}
            >{cell}</button>
          {/if}
        {/each}
      </div>

      {#if withTime}
        <div class="md-df-time">
          <span class="material-symbols-outlined">schedule</span>
          <input type="number" min="0" max="23" bind:value={h} oninput={() => selected && (value = toIso(selected, h, m))} />
          <span>:</span>
          <input type="number" min="0" max="59" bind:value={m} oninput={() => selected && (value = toIso(selected, h, m))} />
        </div>
      {/if}

      <footer class="md-df-actions">
        <button type="button" class="md-df-textbtn" onclick={clear}>Clear</button>
        <button type="button" class="md-df-textbtn primary" onclick={done}>Done</button>
      </footer>
    </div>
  {/if}
</div>

<style>
  .md-df { position: relative; display: block; width: 100%; }

  .md-df-trigger {
    width: 100%;
    height: 56px;
    padding: 0 44px 0 16px;
    background: rgb(var(--md-tf-surface, var(--md-sys-color-surface)));
    border: 1px solid rgb(var(--md-sys-color-outline));
    border-radius: 4px;
    color: rgb(var(--md-sys-color-on-surface));
    font-size: 16px;
    line-height: 24px;
    text-align: left;
    cursor: pointer;
    position: relative;
    font-family: inherit;
    transition: border-color 150ms, border-width 150ms;
  }
  @media (max-width: 768px) {
    .md-df-trigger { height: 48px; font-size: 14px; }
    .md-df-text { line-height: 48px; }
  }
  .md-df-trigger:hover { border-color: rgb(var(--md-sys-color-on-surface)); }
  .md-df-trigger:focus, .md-df-trigger.open {
    outline: none;
    border-color: rgb(var(--md-sys-color-primary));
    border-width: 2px;
    padding: 0 43px 0 15px;
  }
  .md-df-text { display: block; line-height: 56px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .md-df-text.placeholder { color: transparent; }

  .md-df-label {
    position: absolute;
    left: 12px;
    top: 0;
    transform: translateY(-50%);
    padding: 0 4px;
    background: rgb(var(--md-tf-surface, var(--md-sys-color-surface)));
    color: rgb(var(--md-sys-color-on-surface-variant));
    font-size: 12px;
    line-height: 1;
    pointer-events: none;
  }
  .md-df-trigger:focus ~ .md-df-label,
  .md-df-trigger.open ~ .md-df-label { color: rgb(var(--md-sys-color-primary)); }

  .md-df-icon {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: rgb(var(--md-sys-color-on-surface-variant));
    pointer-events: none;
  }

  /* Scrim behind the modal calendar on mobile */
  .md-df-scrim {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.32);
    z-index: 999;
  }

  /* Calendar panel: docked (default) or centered modal (mobile) */
  .md-df-panel {
    position: fixed;
    z-index: 1000;
    width: 320px;
    background: rgb(var(--md-sys-color-surface-container-high));
    color: rgb(var(--md-sys-color-on-surface));
    border-radius: 16px;
    padding: 12px;
    box-shadow:
      0 1px 3px 0 rgba(0, 0, 0, 0.30),
      0 4px 8px 3px rgba(0, 0, 0, 0.15);
  }
  .md-df-panel.mobile {
    /* Center on screen; ignore inline top/left since the scrim covers everything */
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: min(92vw, 340px);
    max-height: 92vh;
    overflow-y: auto;
  }

  .md-df-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 4px 8px;
  }
  .md-df-month { flex: 1; text-align: center; font-weight: 500; }
  .md-df-iconbtn {
    width: 40px; height: 40px;
    display: inline-flex; align-items: center; justify-content: center;
    border: none;
    background: transparent;
    border-radius: 50%;
    color: rgb(var(--md-sys-color-on-surface-variant));
    cursor: pointer;
  }
  .md-df-iconbtn:hover { background: rgb(var(--md-sys-color-on-surface) / 0.08); }

  .md-df-weekdays, .md-df-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
  }
  .md-df-weekdays { padding: 4px 0; color: rgb(var(--md-sys-color-on-surface-variant)); font-size: 12px; text-align: center; }
  .md-df-cell {
    width: 40px; height: 40px;
    display: inline-flex; align-items: center; justify-content: center;
    margin: 2px auto;
    border: none;
    background: transparent;
    border-radius: 50%;
    cursor: pointer;
    color: inherit;
    font-size: 14px;
    font-family: inherit;
  }
  .md-df-cell.empty { cursor: default; }
  .md-df-cell:hover:not(.empty) { background: rgb(var(--md-sys-color-on-surface) / 0.08); }
  .md-df-cell.today {
    border: 1px solid rgb(var(--md-sys-color-primary));
    color: rgb(var(--md-sys-color-primary));
  }
  .md-df-cell.selected {
    background: rgb(var(--md-sys-color-primary));
    color: rgb(var(--md-sys-color-on-primary));
  }
  /* Out-of-range day: visually muted, no hover, no click. Matches the
     standard M3 disabled-state recipe (38% opacity on on-surface). */
  .md-df-cell.disabled {
    color: rgb(var(--md-sys-color-on-surface) / 0.38);
    cursor: not-allowed;
  }
  .md-df-cell.disabled:hover { background: transparent; }

  .md-df-iconbtn:disabled {
    color: rgb(var(--md-sys-color-on-surface) / 0.38);
    cursor: not-allowed;
  }
  .md-df-iconbtn:disabled:hover { background: transparent; }

  .md-df-time {
    display: flex; align-items: center; gap: 8px;
    padding: 12px 8px 4px;
    color: rgb(var(--md-sys-color-on-surface-variant));
  }
  .md-df-time input {
    width: 56px; height: 40px;
    padding: 0 8px;
    background: rgb(var(--md-sys-color-surface));
    border: 1px solid rgb(var(--md-sys-color-outline));
    border-radius: 4px;
    color: rgb(var(--md-sys-color-on-surface));
    text-align: center;
    font-size: 16px;
    font-family: inherit;
  }
  .md-df-time input:focus { outline: none; border-color: rgb(var(--md-sys-color-primary)); border-width: 2px; padding: 0 7px; }

  .md-df-actions {
    display: flex; justify-content: flex-end; gap: 4px;
    padding: 8px 4px 0;
  }
  .md-df-textbtn {
    height: 40px;
    padding: 0 12px;
    border: none;
    background: transparent;
    color: rgb(var(--md-sys-color-on-surface-variant));
    border-radius: 20px;
    font-family: inherit;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
  }
  .md-df-textbtn:hover { background: rgb(var(--md-sys-color-on-surface) / 0.08); }
  .md-df-textbtn.primary { color: rgb(var(--md-sys-color-primary)); }
</style>
